import React from "react";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { addDays, startOfDay, addHours } from "date-fns";
import {
  TIMELINE_DATE_HEADER_HEIGHT,
  TIMELINE_HEADER_HEIGHT,
  TIMELINE_HOUR_HEADER_HEIGHT,
} from "./timelineConstants";

interface TimelineHeaderProps {
  headerRef: React.RefObject<HTMLDivElement>;
  viewStartDate: Date;
  daysToShow: number;
  zoom: number;
  BASE_DAY_WIDTH: number;
  trailingSpacerWidth?: number;
}

const TimelineHeader: React.FC<TimelineHeaderProps> = ({
  headerRef,
  viewStartDate,
  daysToShow,
  zoom,
  BASE_DAY_WIDTH,
  trailingSpacerWidth = 0,
}) => {
  const getTimeIntervals = (zoom: number): number => {
    if (zoom >= 2) return 15;
    if (zoom >= 1.5) return 30;
    if (zoom >= 1) return 60;
    return 120;
  };

  const shouldShowLabel = (interval: number): boolean => {
    if (zoom >= 2) return interval % 4 === 0;
    if (zoom >= 1.5) return interval % 2 === 0;
    return true;
  };

  const renderTimelineHeader = () => {
    const days = [];
    const intervalMinutes = getTimeIntervals(zoom);
    const intervalsPerDay = Math.floor((24 * 60) / intervalMinutes);

    for (let i = 0; i < daysToShow; i++) {
      const date = addDays(viewStartDate, i);
      const dayWidth = BASE_DAY_WIDTH * zoom;

      days.push(
        <div
          key={i}
          className="timeline-day"
          style={{
            width: `${dayWidth}px`,
            minWidth: `${dayWidth}px`,
            position: "relative",
            height: "100%",
            borderRight: "1px solid #e0e0e0",
          }}
        >
          <div
            className="timeline-date"
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              backgroundColor: "#f5f5f5",
              zIndex: 2,
              padding: "8px",
              borderBottom: "1px solid #e0e0e0",
              height: `${TIMELINE_DATE_HEADER_HEIGHT}px`,
              display: "flex",
              alignItems: "center",
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
          >
            {format(date, "EEE, MMM d", { locale: enUS })}
          </div>
          <div
            className="timeline-hours"
            style={{
              display: "flex",
              marginTop: `${TIMELINE_DATE_HEADER_HEIGHT}px`,
              height: `${TIMELINE_HOUR_HEADER_HEIGHT}px`,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            {Array.from({ length: intervalsPerDay }, (_, interval) => {
              const minutes = interval * intervalMinutes;
              const hour = Math.floor(minutes / 60);
              const minute = minutes % 60;
              const time = addHours(startOfDay(date), hour);
              time.setMinutes(minute);

              return (
                <div
                  key={interval}
                  className="timeline-hour text-xs"
                  style={{
                    width: `${dayWidth / intervalsPerDay}px`,
                    flexShrink: 0,
                    textAlign: "center",
                    padding: "0 2px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    borderRight: "1px solid #eee",
                    backgroundColor: "#f5f5f5",
                  }}
                >
                  {shouldShowLabel(interval)
                    ? format(time, "h a", { locale: enUS })
                    : ""}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return days;
  };

  const dayWidth = BASE_DAY_WIDTH * zoom;
  const headerContentWidth = daysToShow * dayWidth + trailingSpacerWidth;

  return (
    <div
      className="timeline-header"
      ref={headerRef}
      style={{
        height: `${TIMELINE_HEADER_HEIGHT}px`,
        overflowX: "hidden",
        position: "sticky",
        top: 0,
        zIndex: 10,
        backgroundColor: "#f5f5f5",
        borderBottom: "1px solid #e0e0e0",
      }}
    >
      <div
        className="timeline-days-container"
        style={{
          display: "flex",
          width: `${headerContentWidth}px`,
          position: "relative",
          height: "100%",
        }}
      >
        {renderTimelineHeader()}
      </div>
    </div>
  );
};

export default TimelineHeader;
